<template>
  <div class="reserve-patient-search">
    <v-text-field
      v-model="keyword"
      :label="$t('ReservePatientSearch.label')"
      :placeholder="$t('ReservePatientSearch.placeholder')"
      prepend-inner-icon="mdi-account-search-outline"
      clearable
      dense
      outlined
      hide-details
      :loading="initialLoading"
      @focus="openPanel"
      @click:clear="resetSearch"
    />

    <v-card
      v-if="panelOpen && trimmedKeyword"
      class="search-panel mt-1"
      outlined
      elevation="8"
    >
      <v-alert v-if="errorMessage" dense text :type="alertType" class="ma-3 mb-1">
        {{ errorMessage }}
      </v-alert>

      <div v-if="initialLoading" class="pa-3">
        <v-skeleton-loader type="list-item-three-line@3" />
      </div>

      <template v-else-if="searched">
        <div v-if="!results.length && !errorMessage" class="pa-6 text-center grey--text text--darken-1">
          {{ $t('ReservePatientSearch.noResults') }}
        </div>

        <div v-for="patient in results" :key="patient.patient_code" class="patient-result">
          <div
            class="patient-summary pa-3"
            role="button"
            tabindex="0"
            :aria-expanded="isPatientExpanded(patient.patient_code) ? 'true' : 'false'"
            @click="togglePatient(patient.patient_code)"
            @keyup.enter="togglePatient(patient.patient_code)"
          >
            <div class="d-flex align-start">
              <div class="flex-grow-1 min-width-0">
                <div class="font-weight-bold text-body-1 text-truncate">
                  {{ patient.name || $t('ReservePatientSearch.unknownPatient') }}
                </div>
                <div class="patient-meta text-caption grey--text text--darken-1 mt-1">
                  <span>
                    <v-icon x-small class="mr-1">mdi-cake-variant-outline</v-icon>
                    {{ formatBirthday(patient.birthday) }}
                  </span>
                  <span>
                    <v-icon x-small class="mr-1">mdi-cellphone</v-icon>
                    {{ patient.phone || $t('ReservePatientSearch.notProvided') }}
                  </span>
                </div>
                <div class="text-body-2 mt-2">
                  <template v-if="patient.appointment_summary">
                    <v-chip x-small outlined color="primary" class="mr-1">
                      {{ relationText(patient.appointment_summary.relation) }}
                    </v-chip>
                    {{ formatSummaryTime(patient.appointment_summary.start_at) }}
                  </template>
                  <span v-else class="grey--text text--darken-1">
                    {{ $t('ReservePatientSearch.noValidAppointment') }}
                  </span>
                </div>
              </div>
              <v-icon class="ml-2">
                {{ isPatientExpanded(patient.patient_code) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>
          </div>

          <reserve-patient-appointments
            v-if="hasOpenedPatient(patient.patient_code)"
            v-show="isPatientExpanded(patient.patient_code)"
            :ref="appointmentsRef(patient.patient_code)"
            :patient-code="patient.patient_code"
            :base-date="baseDate"
            @summary-updated="updatePatientSummary"
            @open-reserve="$emit('open-reserve', $event)"
          />
        </div>

        <div v-if="results.length" class="pa-3 text-center">
          <v-btn
            v-if="hasMore"
            text
            color="primary"
            :loading="loadingMore"
            :disabled="loadingMore"
            @click="loadMore"
          >
            {{ $t('ReservePatientSearch.loadMore') }}
          </v-btn>
          <span v-else class="text-caption grey--text">
            {{ $t('ReservePatientSearch.noMoreResults') }}
          </span>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { searchReservePatientsAPI } from '@/api/reserve'
import ReservePatientAppointments from './ReservePatientAppointments'

export default {
  name: 'ReservePatientSearch',
  components: {
    ReservePatientAppointments
  },
  data() {
    return {
      keyword: '',
      searchedKeyword: '',
      results: [],
      cursor: null,
      hasMore: false,
      baseDate: '',
      initialLoading: false,
      loadingMore: false,
      searched: false,
      errorMessage: '',
      alertType: 'error',
      panelOpen: false,
      expandedPatients: {},
      openedPatients: {},
      debounceTimer: null,
      requestGeneration: 0
    }
  },
  computed: {
    ...mapGetters([
      'axiosParamData'
    ]),
    trimmedKeyword() {
      return String(this.keyword || '').trim()
    }
  },
  watch: {
    keyword() {
      this.scheduleSearch()
    }
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer)
    this.requestGeneration += 1
  },
  methods: {
    scheduleSearch() {
      clearTimeout(this.debounceTimer)
      const generation = ++this.requestGeneration
      const keyword = this.trimmedKeyword

      this.clearResultState()
      if (!keyword) {
        this.panelOpen = false
        return
      }

      this.panelOpen = true
      this.debounceTimer = setTimeout(() => {
        this.searchPatients(keyword, generation)
      }, 400)
    },
    clearResultState() {
      this.searchedKeyword = ''
      this.results = []
      this.cursor = null
      this.hasMore = false
      this.baseDate = ''
      this.initialLoading = false
      this.loadingMore = false
      this.searched = false
      this.errorMessage = ''
      this.alertType = 'error'
      this.expandedPatients = {}
      this.openedPatients = {}
    },
    resetSearch() {
      this.keyword = ''
      clearTimeout(this.debounceTimer)
      this.requestGeneration += 1
      this.clearResultState()
      this.panelOpen = false
    },
    async searchPatients(keyword, generation) {
      if (generation !== this.requestGeneration || keyword !== this.trimmedKeyword) return

      this.initialLoading = true
      this.searchedKeyword = keyword
      await this.requestPatients({keyword, cursor: null, generation, append: false})
    },
    async requestPatients({keyword, cursor, generation, append}) {
      try {
        const response = await searchReservePatientsAPI({
          ...this.axiosParamData,
          keyword,
          cursor
        })

        if (generation !== this.requestGeneration || keyword !== this.trimmedKeyword) return

        const payload = response.data
        if (!payload || !Array.isArray(payload.data) || !payload.meta) {
          throw new Error('Unexpected reserve patient search response')
        }
        if (append && payload.meta.base_date !== this.baseDate) {
          throw new Error('Reserve patient search base_date changed during cursor pagination')
        }

        this.results = append ? this.results.concat(payload.data) : payload.data
        this.baseDate = append ? this.baseDate : payload.meta.base_date
        this.hasMore = payload.meta.has_more === true
        this.cursor = this.hasMore ? payload.meta.next_cursor : null
        this.searched = true
        this.errorMessage = ''
        this.alertType = 'error'
      } catch (error) {
        if (generation !== this.requestGeneration) return
        const searchError = this.resolveSearchError(error)
        this.errorMessage = searchError.message
        this.alertType = searchError.type
        this.searched = true
      } finally {
        if (generation === this.requestGeneration) {
          this.initialLoading = false
          this.loadingMore = false
        }
      }
    },
    loadMore() {
      if (!this.hasMore || !this.cursor || this.loadingMore || this.initialLoading) return

      this.loadingMore = true
      this.requestPatients({
        keyword: this.searchedKeyword,
        cursor: this.cursor,
        generation: this.requestGeneration,
        append: true
      })
    },
    resolveSearchError(error) {
      const response = error?.response
      const errorCode = response?.data?.code
      const status = response?.status

      if (errorCode === 'VALIDATION_ERROR' || status === 400 || status === 422) {
        return {
          message: this.$t('ReservePatientSearch.invalidKeyword'),
          type: 'warning'
        }
      }
      if (status === 429) {
        return {
          message: this.$t('ReservePatientSearch.tooManyRequests'),
          type: 'warning'
        }
      }
      if (error?.isAxiosError && !response) {
        return {
          message: this.$t('ReservePatientSearch.networkError'),
          type: 'error'
        }
      }
      if (status === 404 || status >= 500) {
        return {
          message: this.$t('ReservePatientSearch.serviceUnavailable'),
          type: 'error'
        }
      }
      return {
        message: this.$t('ReservePatientSearch.searchFailed'),
        type: 'error'
      }
    },
    togglePatient(patientCode) {
      if (!this.openedPatients[patientCode]) this.$set(this.openedPatients, patientCode, true)
      this.$set(this.expandedPatients, patientCode, !this.expandedPatients[patientCode])
    },
    isPatientExpanded(patientCode) {
      return this.expandedPatients[patientCode] === true
    },
    hasOpenedPatient(patientCode) {
      return this.openedPatients[patientCode] === true
    },
    appointmentsRef(patientCode) {
      return `appointments-${patientCode}`
    },
    refreshPatient(patientCode) {
      const componentRef = this.$refs[this.appointmentsRef(patientCode)]
      const component = Array.isArray(componentRef) ? componentRef[0] : componentRef
      if (!component) return Promise.resolve()
      return component.refresh()
    },
    updatePatientSummary({patientCode, appointmentSummary}) {
      const index = this.results.findIndex(patient => patient.patient_code === patientCode)
      if (index === -1) return
      this.$set(this.results[index], 'appointment_summary', appointmentSummary)
    },
    formatBirthday(birthday) {
      if (!birthday) return this.$t('ReservePatientSearch.notProvided')
      const matched = String(birthday).match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!matched) return birthday

      const rocYear = Number(matched[1]) - 1911
      return `${rocYear}${this.$t('ReservePatientSearch.rocYear')}${matched[2]}${this.$t('ReservePatientSearch.month')}${matched[3]}${this.$t('ReservePatientSearch.day')}`
    },
    formatSummaryTime(startAt) {
      const date = this.$moment.parseZone(startAt)
      return date.isValid() ? date.format('YYYY/M/D HH:mm') : ''
    },
    relationText(relation) {
      return relation === 'next'
        ? this.$t('ReservePatientSearch.nextAppointment')
        : this.$t('ReservePatientSearch.previousAppointment')
    },
    openPanel() {
      if (this.trimmedKeyword) this.panelOpen = true
    }
  }
}
</script>

<style scoped>
.reserve-patient-search {
  position: relative;
  width: 320px;
  max-width: 100%;
  z-index: 12;
}

.search-panel {
  position: absolute;
  top: 100%;
  left: 0;
  width: 480px;
  max-width: calc(100vw - 32px);
  max-height: min(68vh, 640px);
  overflow-y: auto;
  background: white;
}

.patient-result + .patient-result {
  border-top: 1px solid #eeeeee;
}

.patient-summary {
  cursor: pointer;
}

.patient-summary:hover,
.patient-summary:focus {
  background: #f5f5f5;
  outline: none;
}

.patient-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}

.min-width-0 {
  min-width: 0;
}

@media (max-width: 599px) {
  .reserve-patient-search {
    width: 100%;
  }

  .search-panel {
    width: calc(100vw - 32px);
  }
}
</style>
